import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ConnectorFeatureCollection, ConnectorSummary, NormalizedFeed } from "@chula/shared";
import { API_BASE } from "../lib/apiBase";

export const CONNECTOR_POLL_MS = 60_000;

export interface ConnectorRegistryState {
  connectors: ConnectorSummary[];
  /** True until the first response (or failure). */
  loading: boolean;
  /** Last error; stale connectors stay visible while an error is shown. */
  error: string | null;
  fetchedAt: string | null;
}

export interface ConnectorGeojsonEntry {
  collection: ConnectorFeatureCollection | null;
  loading: boolean;
  error: string | null;
}

async function getJson(url: string, signal: AbortSignal): Promise<unknown> {
  const res = await fetch(url, { signal, cache: "no-store" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`.trim());
  const type = res.headers.get("content-type") ?? "";
  if (!type.toLowerCase().includes("json")) throw new Error(`Expected JSON, received ${type || "unknown content type"}`);
  return res.json();
}

function isRegistry(v: unknown): v is NormalizedFeed<ConnectorSummary> {
  const f = v as NormalizedFeed<ConnectorSummary> | null;
  return !!f && Array.isArray(f.features) && !!f.meta && typeof f.meta.fetchedAt === "string";
}

function isConnectorFc(v: unknown): v is ConnectorFeatureCollection {
  const f = v as ConnectorFeatureCollection | null;
  return !!f && f.type === "FeatureCollection" && Array.isArray(f.features) && !!f.meta;
}

/** GET /api/connectors — the data-plane registry, polled. `enabled=false` pauses it. */
export function useConnectorRegistry(enabled = true, pollMs = CONNECTOR_POLL_MS): ConnectorRegistryState & { refetch: () => void } {
  const [state, setState] = useState<ConnectorRegistryState>({ connectors: [], loading: true, error: null, fetchedAt: null });
  const runRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let ctrl: AbortController | null = null;
    const run = () => {
      ctrl?.abort();
      const mine = new AbortController();
      ctrl = mine;
      getJson(`${API_BASE}/api/connectors`, mine.signal)
        .then((json) => {
          if (cancelled) return;
          if (!isRegistry(json)) throw new Error("Invalid registry response");
          setState({ connectors: json.features, loading: false, error: null, fetchedAt: json.meta.fetchedAt });
        })
        .catch((err: Error) => {
          if (cancelled || err.name === "AbortError") return;
          setState((prev) => ({ ...prev, loading: false, error: err.message || "Registry unavailable" }));
        });
    };
    runRef.current = run;
    run();
    const id = setInterval(run, pollMs);
    return () => { cancelled = true; clearInterval(id); ctrl?.abort(); };
  }, [enabled, pollMs]);

  const refetch = useCallback(() => runRef.current(), []);
  return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}

/**
 * GET /api/connectors/:id/geojson for each connector id in `ids`, polled.
 * Only fetches what is asked for — pass the connectors whose map layer is on.
 */
export function useConnectorGeojson(ids: readonly string[], pollMs = CONNECTOR_POLL_MS): Record<string, ConnectorGeojsonEntry> {
  const [entries, setEntries] = useState<Record<string, ConnectorGeojsonEntry>>({});
  const key = [...ids].sort().join("|");

  useEffect(() => {
    const wanted = key ? key.split("|") : [];
    setEntries((prev) => {
      const next: Record<string, ConnectorGeojsonEntry> = {};
      for (const id of wanted) next[id] = prev[id] ?? { collection: null, loading: true, error: null };
      return next;
    });
    if (wanted.length === 0) return;
    let cancelled = false;
    const ctrls = new Set<AbortController>();
    const fetchOne = (id: string) => {
      const ctrl = new AbortController();
      ctrls.add(ctrl);
      getJson(`${API_BASE}/api/connectors/${encodeURIComponent(id)}/geojson`, ctrl.signal)
        .then((json) => {
          if (cancelled) return;
          if (!isConnectorFc(json)) throw new Error("Invalid GeoJSON response");
          setEntries((prev) => ({ ...prev, [id]: { collection: json, loading: false, error: null } }));
        })
        .catch((err: Error) => {
          if (cancelled || err.name === "AbortError") return;
          setEntries((prev) => ({
            ...prev,
            [id]: { collection: prev[id]?.collection ?? null, loading: false, error: err.message || "Unavailable" },
          }));
        })
        .finally(() => ctrls.delete(ctrl));
    };
    const runAll = () => wanted.forEach(fetchOne);
    runAll();
    const t = setInterval(runAll, pollMs);
    return () => {
      cancelled = true;
      clearInterval(t);
      for (const c of ctrls) c.abort();
    };
  }, [key, pollMs]);

  return entries;
}

/** Registry + GeoJSON for the connector layers that are switched on. */
export function useConnectors(activeConnectorIds: readonly string[], pollMs = CONNECTOR_POLL_MS) {
  const registry = useConnectorRegistry(true, pollMs);
  const geojson = useConnectorGeojson(activeConnectorIds, pollMs);
  return useMemo(() => ({ registry, geojson }), [registry, geojson]);
}
